import {
  Box,
  Button,
  Flex,
  GreyGrey10,
  GreyGrey60,
  GreyGrey80,
  IconPencil,
  IconXMark,
  Primary12Regular,
  Primary13Regular,
  Primary13SemiBold,
  TextOverflow
} from '@sequencehq/core-components'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { ContractReferenceType } from 'Cube/domain/cube.domain.types'
import { ContractReferenceModal } from 'Cube/view/common/widgets/Pricing/Phase/contractReferences/contractReference.modal'
import { ContractReferenceLink } from 'Cube/view/common/widgets/Pricing/Phase/contractReferences/ContractReferenceLink'
import { usePhaseContractReferences } from 'Cube/view/common/widgets/Pricing/Phase/contractReferences/usePhaseContractReferences'
import { usePhaseDuration } from 'Cube/view/common/widgets/Pricing/Phase/usePhaseDuration'
import { usePhaseName } from 'Cube/view/common/widgets/Pricing/Phase/usePhaseName'
import { FC, useState } from 'react'

import { PhaseContractReferencesCopy } from './PhaseContractReferencesWidget.constants'

/**
 * One integration's references, phase by phase, for its inspector tab. These
 * edit the same references as the phase header badges, and save with the
 * schedule.
 */
export const PhaseContractReferencesWidget: FC<{
  type: Extract<ContractReferenceType, 'SALESFORCE_OPPORTUNITY' | 'NETSUITE'>
  copy: PhaseContractReferencesCopy
  'data-testid': string
}> = props => {
  const cubeCtx = useCubeContext()
  const phases = cubeCtx.queries.orderedPhases
  const isVisible = phases.some(
    phase =>
      cubeCtx.queries.availableFeatures.phases[phase.id].phase
        .contractReferences.available.visible
  )

  if (!isVisible) {
    return null
  }

  return (
    <Flex flexDirection="column" gap="8px" data-testid={props['data-testid']}>
      <Box color={GreyGrey80} {...Primary13SemiBold}>
        {props.copy.title}
      </Box>
      {phases.map(phase => (
        <PhaseReferenceRow
          key={phase.id}
          phaseId={phase.id}
          type={props.type}
          copy={props.copy}
          data-testid={`${props['data-testid']}.${phase.id}`}
        />
      ))}
    </Flex>
  )
}

const PhaseReferenceRow: FC<{
  phaseId: string
  type: ContractReferenceType
  copy: PhaseContractReferencesCopy
  'data-testid': string
}> = props => {
  const contractReferences = usePhaseContractReferences({
    phaseId: props.phaseId
  })
  const phaseTitle = usePhaseTitle({ phaseId: props.phaseId })
  const [isEditing, setIsEditing] = useState(false)

  const indexedReference = contractReferences.references.find(
    ({ reference }) => reference.type === props.type
  )
  const canAdd =
    !indexedReference &&
    contractReferences.canEdit &&
    contractReferences.addableTypes.includes(props.type)

  return (
    <Flex
      flexDirection="column"
      gap="12px"
      padding="12px"
      borderRadius="8px"
      backgroundColor={GreyGrey10}
      data-testid={props['data-testid']}
    >
      <Flex alignItems="flex-start" gap="8px">
        <Flex flexDirection="column" gap="4px" minWidth="0" flex="1">
          <Box color={GreyGrey80} {...Primary13SemiBold}>
            <TextOverflow>{phaseTitle}</TextOverflow>
          </Box>
          {indexedReference ? (
            <Box
              color={GreyGrey80}
              {...Primary13Regular}
              data-testid={`${props['data-testid']}.value`}
            >
              <TextOverflow>{indexedReference.reference.label}</TextOverflow>
            </Box>
          ) : (
            <Box
              color={GreyGrey60}
              {...Primary12Regular}
              data-testid={`${props['data-testid']}.empty`}
            >
              {props.copy.emptyText}
            </Box>
          )}
        </Flex>
        {indexedReference && (
          <Flex gap="2px" flexShrink={0}>
            <ContractReferenceLink
              reference={indexedReference.reference}
              size="24px"
              data-testid={`${props['data-testid']}.link`}
            />
            {contractReferences.canEdit && (
              <>
                <Button
                  variant="ghost"
                  size="24px"
                  aria-label={`Change ${props.copy.entityLabel}`}
                  icon={<IconPencil color={GreyGrey60} />}
                  onClick={() => setIsEditing(true)}
                  data-testid={`${props['data-testid']}.change`}
                />
                <Button
                  variant="ghost"
                  size="24px"
                  aria-label={`Remove ${props.copy.entityLabel}`}
                  icon={<IconXMark color={GreyGrey60} />}
                  onClick={() =>
                    contractReferences.removeReference(indexedReference.index)
                  }
                  data-testid={`${props['data-testid']}.remove`}
                />
              </>
            )}
          </Flex>
        )}
      </Flex>
      {canAdd && (
        <Button
          variant="secondary"
          fullWidth
          onClick={() => setIsEditing(true)}
          data-testid={`${props['data-testid']}.add`}
        >
          {props.copy.addLabel}
        </Button>
      )}
      {isEditing && (
        <ContractReferenceModal
          phaseId={props.phaseId}
          type={props.type}
          index={indexedReference?.index}
          onClose={() => setIsEditing(false)}
        />
      )}
    </Flex>
  )
}

/** The phase's name, or its dates when it has none. */
const usePhaseTitle = (props: { phaseId: string }) => {
  const phaseName = usePhaseName(props)
  const phaseDuration = usePhaseDuration(props)

  if (phaseName.name) {
    return phaseName.name
  }

  const hasDates = Boolean(
    phaseDuration.startDateFormatted && phaseDuration.endDateFormatted
  )

  return hasDates
    ? `${phaseDuration.startDateFormatted} – ${phaseDuration.endDateFormatted}`
    : phaseDuration.differenceFormatted
}
